/**
 * Prescription API client (T33 — real backend wired).
 *
 * Backend owns the 15-state machine and projects it to the 3-value FE
 * contract. The UI calls semantic endpoints (createFromEncounter / finalize
 * / cancel); it never drives a raw state. e-sign + gov-Rx are Pro (ADR-0015)
 * and absent from this client by design.
 */
import { apiClient } from "./client";
import { MOCKS_ENABLED } from "./mock/registry";
import { mockPrescriptions } from "./mock/prescriptions";
import type {
  PrescriptionDetail,
  PrescriptionItem,
  PrescriptionListFilter,
  PrescriptionSummary,
} from "../types/prescription";
import type { PageResponse } from "../types/patient";

const realClient = {
  list(
    filter: PrescriptionListFilter | undefined,
    pageable: { page?: number; size?: number }
  ): Promise<PageResponse<PrescriptionSummary>> {
    const params: Record<string, string | number> = {};
    if (filter?.q) params.q = filter.q;
    if (filter?.status) params.status = filter.status;
    if (filter?.doctorId) params.doctorId = filter.doctorId;
    if (filter?.patientId) params.patientId = filter.patientId;
    if (filter?.fromDate) params.fromDate = filter.fromDate;
    if (filter?.toDate) params.toDate = filter.toDate;
    if (pageable.page != null) params.page = pageable.page;
    if (pageable.size != null) params.size = pageable.size;
    return apiClient
      .get<PageResponse<PrescriptionSummary>>("/v1/prescriptions", { params })
      .then((r) => r.data);
  },

  get(id: string): Promise<PrescriptionDetail> {
    return apiClient
      .get<PrescriptionDetail>(`/v1/prescriptions/${id}`)
      .then((r) => r.data);
  },

  createFromEncounter(encounterId: string): Promise<PrescriptionDetail> {
    return apiClient
      .post<PrescriptionDetail>("/v1/prescriptions", { encounterId })
      .then((r) => r.data);
  },

  updateItems(
    id: string,
    items: Omit<PrescriptionItem, "id" | "itemOrder" | "medicineName" | "medicineStrength">[]
  ): Promise<void> {
    return apiClient
      .put<void>(`/v1/prescriptions/${id}/items`, items)
      .then(() => undefined);
  },

  finalize(id: string): Promise<void> {
    return apiClient
      .post<void>(`/v1/prescriptions/${id}/finalize`)
      .then(() => undefined);
  },

  cancel(id: string, reason: string): Promise<void> {
    return apiClient
      .post<void>(`/v1/prescriptions/${id}/cancel`, { reason })
      .then(() => undefined);
  },
};

export const prescriptionsApi = MOCKS_ENABLED ? mockPrescriptions : realClient;

export const prescriptionQueryKeys = {
  all: ["prescriptions"] as const,
  list: (filter: unknown, page: number) =>
    ["prescriptions", "list", filter, page] as const,
  detail: (id: string) => ["prescriptions", "detail", id] as const,
};
